import UserRow from "../people/UserRow";
import { PeopleListSkeleton } from '../ui/skeletons';
import FitText from '../ui/FitText';

// Mobile gesture-aware People drawer (the small-screen sibling of PeoplePanel).
// Structural subsets of ChatLayout's types — the richer activity/status/roles
// fields are accessed via `as any`, mirroring the original inline JSX.
type Space = { id: string; name?: string | null; avatarUrl?: string | null };
type PeopleMember = {
  id: string;
  name?: string | null;
  username?: string | null;
  avatarUrl?: string | null;
  nameColor?: string | null;
  onMobile?: boolean;
  isIdle?: boolean;
};

type MobilePeopleSheetProps = {
  usersDrawerProgress: number;
  swipePanel: null | 'chan' | 'users';
  voids: Space[];
  currentVoidId: string;
  headerSpaceLabel: string;
  isDmSpace: boolean;
  currentSpaceOnlineCount: number;
  channelVisibleMembersWithPresence: PeopleMember[];
  onlineChannelMembers: PeopleMember[];
  offlineChannelMembers: PeopleMember[];
  /** Members may still be arriving: show skeleton rows instead of "No one else". */
  loading?: boolean;
  onClose: () => void;
  onOpenProfile: (id: string) => void;
};

function MobilePeopleSheet({
  usersDrawerProgress,
  swipePanel,
  voids,
  currentVoidId,
  headerSpaceLabel,
  isDmSpace,
  currentSpaceOnlineCount,
  channelVisibleMembersWithPresence,
  onlineChannelMembers,
  offlineChannelMembers,
  loading = false,
  onClose,
  onOpenProfile,
}: MobilePeopleSheetProps) {
  return (
    <div className="md:hidden fixed inset-0 z-40">
      <div
        className="absolute inset-0 bg-black/70"
        style={{
          opacity: usersDrawerProgress * 0.6,
          transition: 'opacity 220ms ease-out',
          pointerEvents: usersDrawerProgress > 0.01 ? 'auto' : 'none',
        }}
        onClick={onClose}
      />
      <div
        className="absolute inset-y-0 right-0 w-4/5 max-w-sm mobile-sheet border-l border-white/10 shadow-lg shadow-black/60 flex flex-col"
        style={{
          transform: `translateX(${(1 - usersDrawerProgress) * 100}%)`,
          transition: swipePanel === 'users' ? 'none' : 'transform 320ms ease-out',
          willChange: 'transform',
        }}
      >
        <div className="px-5 pt-4 pb-3 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-fg-3">People</p>
            <p className="flex min-w-0 items-baseline text-white">
              <FitText className="font-medium" maxRem={1.125} minRem={0.8}>{voids.find((v) => v.id === currentVoidId)?.name || headerSpaceLabel}</FitText>
              {!isDmSpace && currentSpaceOnlineCount > 0 && (
                <span className="ml-2 shrink-0 whitespace-nowrap text-sm text-online-light/80">• {currentSpaceOnlineCount} online</span>
              )}
            </p>
          </div>
          <button className="rounded-full border border-white/10 p-2 text-fg-2 hover:text-white" onClick={onClose} aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          </div>
            <div className="flex-1 overflow-auto px-5 py-4 space-y-2">
            {channelVisibleMembersWithPresence.length === 0 && (loading ? <PeopleListSkeleton /> : <div className="text-fg-3 text-sm px-2">No one else is in this channel</div>)}
            {onlineChannelMembers.map((m) => (
              <div key={m.id} className="rounded-2xl border border-white/5 bg-white/5 px-2 py-1">
                <UserRow
                  data={{ id: m.id, name: m.name, username: m.username, avatarUrl: m.avatarUrl, nameColor: m.nameColor, status: (m as any).activity || undefined, rawStatus: (m as any).status, online: true, onMobile: m.onMobile, isIdle: m.isIdle, roles: (m as any).roles || [] }}
                  onClick={() => onOpenProfile(m.id)}
                />
              </div>
            ))}
            {offlineChannelMembers.length > 0 && (
              <>
                <div className="text-meta text-fg-3 px-2 pt-2">Offline</div>
                {offlineChannelMembers.map((m) => (
                  <div key={m.id} className="rounded-2xl border border-white/5 bg-white/5 px-2 py-1">
                    <UserRow
                      data={{ id: m.id, name: m.name, username: m.username, avatarUrl: m.avatarUrl, nameColor: m.nameColor, status: (m as any).activity || undefined, rawStatus: (m as any).status, online: false, onMobile: m.onMobile, isIdle: m.isIdle, roles: (m as any).roles || [] }}
                      onClick={() => onOpenProfile(m.id)}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
  );
}

export default MobilePeopleSheet;
